/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { AttachmentStatus, Roles, WorkOrderStatus } from '../../API';
import { BillingInfo } from '../../Modules/Customer/Models/BidApproval.Model';
import { Icons } from './Icons';

export const BidOverdueStatus = 'Bid overdue';

export enum UserType {
  Associate = 'Associate',
  Customer = 'Customer',
  Vendor = 'Vendor',
  SubVendor = 'Sub vendor',
  PrimeVendor = 'Prime Vendor',
  NoUser = '',
}

export enum Role {
  Supervisor = 'Supervisor',
  Admin = 'Admin',
  Associate = 'Associate/Technician',
  AccountHolder = 'AccountHolder',
  RegionalManager = 'RegionalManager',
  DistrictManager = 'DistrictManager',
  Customer = 'Customer',
  OTJAdmin = 'OTJAdmin',
}

export enum S3BucketLevel {
  private = 'private',
  public = 'public',
}

export enum AWSAuthMode {
  API_KEY = 'API_KEY',
  COGNITO = 'AMAZON_COGNITO_USER_POOLS',
}

export enum ChipStatus {
  'Facilities' = 'homePage:facilities',
  'No Show' = 'homePage:noShow',
  'Incomplete' = 'Incomplete',
  'In progress' = 'homePage:inProgress',
  'Complete' = 'homePage:complete',
  'Pending Reviews' = 'homePage:pendingReviews',
  'Approved' = 'homePage:approved',
  'NoJob' = 'homePage:noJob',
  'JobScheduled' = 'homePage:jobScheduled',
}

export enum StatusMap {
  noShow = 'No Show',
  inComplete = 'Incomplete',
  inProgress = 'In progress',
  complete = 'Complete',
  pendingReviews = 'Pending Reviews',
  approved = 'Approved',
}

export enum MasterData {
  AdditionalExpenses = 'OTJ_ADDITIONAL_EXPENSES',
  FacilityTypes = 'FACILITY_TYPE',
  PreShiftCheckList = 'PRESHIFT_CHECKLIST',
  BID_DUE_DAYS = 'BID_DUE_DAYS',
  EMAIL_EDIT_ALLOWED_USERS = 'EMAIL_EDIT_ALLOWED_USERS',
  CREATE_JOB_ALLOWED_CUSTOMERS = 'CREATE_JOB_ALLOWED_CUSTOMERS',
  ServiceType = 'SERVICE_TYPE',
  DASHBOARD_NUMBER = 'DASHBOARD_NUMBER',
  VELOCITI_ORG_ID = 'VELOCITI_ORG_ID',
  UNIFI_ORG_ID = 'UNIFI_ORG_ID',
}

// Role stored against the DASHBOARD_NUMBER master data rows for prime vendor reports.
export const PRIME_VENDOR_ROLE = 'PrimeVendor';

export const PEAZY_PLATFORM_CHARGE = 'PEAZY_PLATFORM_PRICE';

export enum CurrencySymbol {
  Dollar = '$',
}

export enum JobStatus {
  PendingCustomerApproval = 'Pending Customer Approval',
  Awarded = 'Awarded',
  PendingEstimation = 'Pending Estimation',
  Draft = 'Draft',
  RevisionRequested = 'Customer Rejected',
  OpenForBids = 'Open for bids',
  ClosedForBid = BidOverdueStatus,
  OpenToBid = 'OpenToBid',
  InProgress = 'InProgress',
}

export enum TransactionStatus {
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Rejected = 'Rejected',
  ContinueQuoteUpdate = 'Continue quote update',
  PendingApprovalFromCustomer = 'Pending approval from customer',
  CustomerRejectedTheBid = 'Customer rejected the bid',
}

export enum JobStatuses {
  Draft = 'Draft',
  Awarded = 'Awarded',
  OpenToBid = 'OpenToBid',
  Completed = 'Completed',
  Incomplete = 'Incomplete',
  InProgress = 'InProgress',
  Closed = 'Closed',
  Expired = 'Expired',
  Rejected = 'Rejected',
  PendingEstimation = 'Pending Estimation',
  PendingCustomerApproval = 'Pending Customer Approval',
  PendingApproval = 'PendingCustomerApproval',
  ClosedForBid = BidOverdueStatus,
  Created = 'Created',
  Upcoming = 'Upcoming',
  Inprogress = 'Inprogress',
  Accepted = 'Accepted',
  OpenForBids = 'Open for bids',
}

export interface DataStatus {
  InProgress: string;
  Accepted: string;
  Expired: string;
  InComplete: string;
  Incomplete: string;
  OpenToBid: string;
  Draft: string;
  Completed: string;
  ClosedForBid: string;
  Awarded: string;
  Created: string;
  PendingCustomerApproval: string;
}

export const DATA_STATUS: DataStatus = {
  InProgress: 'In-Progress',
  Accepted: 'Accepted',
  Expired: 'Expired',
  InComplete: 'In-Complete',
  Incomplete: 'In-complete',
  OpenToBid: 'Open To bids',
  Draft: 'Draft',
  Completed: 'Completed',
  ClosedForBid: BidOverdueStatus,
  Awarded: 'Awarded',
  Created: 'Awarded',
  PendingCustomerApproval: 'Pending Customer Approval',
};

export const DocumentType = {
  PDF: 'application/pdf',
  Image: 'image',
  Video: 'video',
  Document: 'document',
};

export enum FileContextType {
  CustomerApproval = 'CustomerApproval',
  Invoice = 'Invoice',
  JobAttachment = 'JobAttachment',
  VendorQuote = 'VendorQuote',
}

export const numberOfTasksPerBatch = 8;

export enum ReworkShiftType {
  CurrentShift = 'CurrentShift',
  NextWorkDay = 'NextWorkDay',
  SpecificDay = 'SpecificDay',
}

export const enum MediaType {
  Photo = 'photo',
  Video = 'video',
  Pdf = 'pdf',
  Document = 'document',
}

export const enum MediaDirection {
  Previous = 'previous',
  Next = 'next',
}

export const enum SortByType {
  DESC = 'DESC',
  ASC = 'ASC',
}

export interface IProofsProps {
  comment?: string | null;
  fileExtension?: string;
  fileName?: string;
  id?: string | null;
  mediaType: string;
  url?: string;
  mdAttachmentType?: string;
}

export interface IUser {
  dataStatus?: string;
  email?: string;
  organizationId?: string;
  phoneNumber?: string;
  roles?: Array<Roles>;
  signedUp?: boolean;
  teamId?: string;
  userId?: string;
  username?: string;
}
export enum DraftStatus {
  C = 'C',
  Y = 'Y',
}

export enum DraftStep {
  SelectFacility = 'SelectFacility',
  AddServices = 'AddServices',
  ServicesDetails = 'ServicesDetails',
  FacilitySpecifics = 'FacilitySpecifics',
  Emergency = 'Emergency',
  JobSummary = 'JobSummary',
  Frequency = 'Frequency',
  Vendors = 'Vendors',
  DurationAndDetails = 'DurationAndDetails',
  SearchForVendor = 'SearchForVendor',
  ServicePhotosAndVideos = 'ServicePhotosAndVideos',
  SelectCustomer = 'SelectCustomer',
  JobExecution = 'JobExecution',
}
export interface Job {
  jobId: string;
  jobName: string;
  startDate?: string | null;
  stopDate?: string | null;
  dataStatus?: string | null;
  specialInstructions?: string | null;
  draftStep?: DraftStep | null;
  draftStatus?: DraftStatus | null;
  jobType?: JobType | null;
  bidCount?: number | null;
  createdBy?: string | null;
  createdByName?: string | null;
  jobDueDates?: string;
}

export enum JobType {
  OneTime = 'OneTime',
  Emergency = 'Emergency',
  Recurring = 'Recurring',
  Periodic = 'Periodic',
}

export enum AppDefaults {
  BidName = 'Untitled',
  Complete = 'Complete',
  Interested = 'Interested',
  NotInterested = 'NotInterested',
}

export enum ApproverType {
  Vendor = 'Vendor',
  Customer = 'Customer',
  PrimeVendor = 'PrimeVendor',
}

export enum Environment {
  Dev = 'dev',
  QA = 'qa',
  Staging = 'staging',
  Prod = 'prod',
  Int = 'int',
  PreProd = 'pre-prod',
  Sandbox = 'sandbox',
}

export enum YearRange {
  Start = 2023,
  End = 2099,
}

export const XMLHttpRequestResponseType = {
  arraybuffer: 'arraybuffer',
  blob: 'blob',
  document: 'document',
  json: 'json',
  text: 'text',
};

export interface States {
  [abbreviation: string]: string;
}

export const states: States = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const IPAddressAPI = 'https://api.ipify.org?format=json';

export const NUMBER_OF_ITEMS_PER_PAGE_IN_FACILITY_TYPES = 20;

export const PO_ATTACHMENTS_FILE_CONTEXT = 'POAttachments';

export enum PromiseAllSettledResponseStatus {
  Fulfilled = 'fulfilled',
  Rejected = 'rejected',
}

export enum TaskStatus {
  Scheduled = 'Scheduled',
  InProgress = 'InProgress',
  Complete = 'Complete',
  Rejected = 'Rejected',
  Rework = 'Rework',
  OnHold = 'OnHold',
  ApprovedBySupervisor = 'ApprovedBySupervisor',
  ApprovedByCustomer = 'ApprovedByCustomer',
  RejectedBySupervisor = 'RejectedBySupervisor',
  RejectedByCustomer = 'RejectedByCustomer',
  PendingCustomerReview = 'PendingCustomerReview',
  ApprovedByPrimeVendor = 'ApprovedByPrimeVendor',
  RejectedByPrimeVendor = 'RejectedByPrimeVendor',
  PendingPrimeVendorApproval = 'PendingPrimeVendorApproval',
  PendingSupervisorApproval = 'PendingSupervisorApproval',
  IssueRaised = 'Issue raised',
}

export enum ProfitMargin {
  Percentage = 1000,
  Dollar = 100000,
}
export enum PricingOptions {
  Unit = 'Unit',
  FlatRate = 'Flat Rate',
  Sqft = 'Sqft',
  Hours = 'Hours',
}

export enum GraphFilterOptions {
  today = 'today',
  week = 'week',
  month = 'month',
  quarter = 'quarter',
}

export enum ByGraphFilterOptions {
  day = 'dayOfTheWeek',
  month = 'month',
}

export enum SubmittedBidStatus {
  PendingForCustomerApproval = 'Pending for customer approval',
  RevisionRequested = 'Revision requested',
  Rejected = 'Rejected',
  Awarded = 'Awarded',
}

export enum Language {
  English = 'en',
  Spanish = 'es',
}

export enum IJobType {
  Emergency = 'Emergency',
  OneTime = 'OneTime',
  Recurring = 'Recurring',
}

export enum ModifyQuoteStatus {
  ContinueQuoteUpdate = 'Continue quote update',
  PendingApprovalFromCustomer = 'Pending approval from customer',
  CustomerRejectedTheBid = 'Customer rejected the bid',
}

export enum StorageKeys {
  Auth = 'auth',
  customerDetails = 'customerDetails',
  LoginToken = 'loginToken',
}

export enum BidSubmitStatus {
  saveAndExit = 'saveAndExit',
  updateAndSubmit = 'updateAndSubmit',
}

export enum Units {
  sqft = 'sqft',
}

export const CustomHeaders = {
  'X-PZY-Client-Type': 'Web',
};

export type AuthHeader = {
  Authorization: string;
};

export enum PlatformType {
  Unknown = 'U',
  Web = 'W',
  MobileIOS = 'MI',
  MobileAndroid = 'MA',
  AdminPortal = 'WAP',
}

export const TagNames = {
  RejectOrRequestRevision: 'rejectOrRequestRevision',
  SelectBid: 'selectBid',
};

export const ExecutionType = {
  SubVendor: 'subVendor',
  Self: 'self',
};

export const EstimationType = {
  EstimateWithinPeazy: 'estimateWithinPeazy',
  EstimateOutsidePeazy: 'estimateOutsidePeazy',
};

export enum DateFormat {
  StandardDate = 'MM/DD/YYYY',
  EuropeanDate = 'DD/MM/YYYY',
  ISODate = 'YYYY-MM-DD',
  ShortMonthFormat = 'MMM D, YYYY',
  DateTimeFormat = 'YYYY-MM-DDTHH:mm:ss.sss',
  DateTimeWithMidnightTimeFormat = 'YYYY-MM-DDT00:00:00.000',
  USDateFormat = 'MM-DD-YYYY',
}

export enum Meridiem {
  AM = 'AM',
  PM = 'PM',
}

export enum JobTypes {
  OneTime = 'One time',
  Emergency = 'Emergency',
  Recurring = 'Recurring',
}

export enum PreShiftChecklistTemplateItemActionTypeNew {
  Brought = 'toBeBrought',
  Provided = 'provided',
  PickedUp = 'buyFromBestBuy',
}

export enum DateRangeOptionType {
  Days = 'days',
  CustomDateRange = 'customDateRange',
}

export enum LegalName {
  vendorLegalName = 'vendorLegalName',
  customerLegalName = 'customerLegalName',
}

export enum HeaderLegalName {
  VendorName = 'Vendor name',
  CustomerName = 'Customer name',
}

export const NumberOfRowsOptions = [
  { id: 1, label: '10', value: '10' },
  { id: 2, label: '20', value: '20' },
  { id: 3, label: '50', value: '50' },
];

export enum ActionTypes {
  TextDetails = 'altTexts:viewDetails',
  NoDataYet = 'noData:noDataYet',
  NoDataComeBack = 'noData:comeBackLater',
}
export const GraphTypes = {
  OneTimeJob: 'OneTimeJob',
  NoShows: 'NoShows',
  CompletionTrend: 'CompletionTrend',
};

export enum DefaultOptions {
  All = 'All',
}

export enum EmailSubscriptionOptions {
  Hourly = 'Hourly',
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
}

export enum DaysOptions {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
}

export enum MonthTimingOptions {
  First = 'First',
  Last = 'Last',
  Midpoint = '15th (Midpoint)',
}

export enum CognitoUserAuthenticationStatus {
  ChangePassword = 'CHANGE_PASSWORD',
  SignIn = 'SIGN_IN',
  SignOut = 'SIGN_OUT',
}

export enum FormFieldType {
  DropDown = 'DropDown',
}

export const FilterName = {
  Associates: 'associateAndVendorFilter',
  Location: 'locationFilter',
  CustomerAndVendor: 'customerAndVendor',
  Jobs: 'jobs',
};

export const FiltersOfAllJobs = {
  JobName: 'jobName',
  JobNumber: 'jobNumber',
  Location: 'locationFilter',
  Customer: 'customer',
  VendorName: 'vendorName',
  VendorNumber: 'vendorNumber',
  ScheduleName: 'scheduleName',
  VendorAdminName: 'vendorAdminName',
  ScheduleStartDate: 'scheduleStartDate',
  ScheduleEndDate: 'scheduleEndDate',
  JobStartDate: 'jobStartDate',
  JobEndDate: 'jobEndDate',
  ScheduleStartTime: 'scheduleStartTime',
  ScheduleEndTime: 'scheduleEndTime',
  VendorAdminEmail: 'vendorAdminEmail',
  CreatedOn: 'createdOn',
  JobAddress: 'jobAddress',
  StateCode: 'stateCode',
  City: 'city',
  WorkTicketNumber: 'workTicketNumber',
  MdScheduleType: 'mdScheduleType',
  WorkOrderStatus: 'workOrderStatus',
  Vendor: 'vendor',
};

export const BooleanTypes = {
  True: 'true',
  False: 'false',
};

export enum ChatURL {
  // dev
  ApiBaseUrlDev = 'https://chat-api-dev.abrightlab.com',
  MqttClusterBaseUrlDev = 'wss://emqx-nlb-dev.abrightlab.com',

  // qa
  ApiBaseUrlQa = 'https://chat-api-qa.abrightlab.com',
  MqttClusterBaseUrlQa = 'wss://emqx-nlb-qa.abrightlab.com',

  // staging
  ApiBaseUrlStaging = 'https://chat-api-stage.abrightlab.com',
  MqttClusterBaseUrlStaging = 'wss://emqx-nlb-stage.abrightlab.com',

  // pre-prod
  ApiBaseUrlPreProd = 'https://chat-api-preprod.peazy.com',
  MqttClusterBaseUrlPreProd = 'wss://emqx-nlb-preprod.peazy.com',

  // prod
  ApiBaseUrlProd = 'https://chat-api.peazy.com',
  MqttClusterBaseUrlProd = 'wss://emqx-nlb.peazy.com',
}

export enum ConversationTabs {
  DIRECT_CHAT = 'Direct',
  GROUP_CHAT = 'Group',
  JOB_CHAT = 'Job',
  SUPPORT_CHAT = 'SUPPORT_CHAT',
}
export const BooleanConstants = {
  True: true,
  False: false,
};

export const enum SortOrderType {
  DESC = 'desc',
  ASC = 'asc',
}

export type FilterItem = {
  id: string;
  value: string;
};

export enum DayOfWeek {
  Su = 0,
  Mo = 1,
  Tu = 2,
  We = 3,
  Th = 4,
  Fr = 5,
  Sa = 6,
}

export const ScheduleTypes = {
  Daily: 'Daily',
  Weekly: 'Weekly',
  Monthly: 'Monthly',
  Annual: 'Annual',
  BiAnnual: 'Bi-Annual',
  Quarterly: 'Quarterly',
  Fortnightly: 'Fortnightly',
  BiMonthly: 'Bi-Monthly',
};

export const ShiftTypes = {
  Day: 'Day',
  Night: 'Night',
  DayAndNight: 'Day & Night',
};

export enum ScheduleStatus {
  Scheduled = 'Scheduled',
  InProgress = 'InProgress',
  Disrupted = 'Disrupted',
  RejectedByVendor = 'Rejected By Vendor',
  RejectedByCustomer = 'Rejected By Customer',
  Completed = 'Completed',
  InComplete = 'InComplete',
  Missed = 'Missed',
  Skipped = 'Skipped',
}

export const SCHEDULE_STATUS_MAP: Record<string, ScheduleStatus> = {
  A: ScheduleStatus.Scheduled,
  P: ScheduleStatus.InProgress,
  W: ScheduleStatus.Disrupted,
  V: ScheduleStatus.RejectedByVendor,
  R: ScheduleStatus.RejectedByCustomer,
  S: ScheduleStatus.Completed,
  I: ScheduleStatus.InComplete,
  N: ScheduleStatus.Missed,
  B: ScheduleStatus.Skipped,
};

export enum DateRangeOptionIds {
  Today = 1,
  NextWeek = 2,
  LastWeek = 3,
  CustomDateRange = 4,
}

export const defaultDateRangeOption = '1';

export const filterConfigs = [
  { title: 'Job name', name: FiltersOfAllJobs.JobName, key: 'jobName' },
  { title: 'Job number', name: FiltersOfAllJobs.JobNumber, key: 'jobNumber' },
  { title: 'Vendor name', name: FiltersOfAllJobs.VendorName, key: 'vendorName' },
  { title: 'Vendor number', name: FiltersOfAllJobs.VendorNumber, key: 'vendorNumber' },
  { title: 'Job address', name: FiltersOfAllJobs.JobAddress, key: 'jobAddress' },
  { title: 'State', name: FiltersOfAllJobs.StateCode, key: 'stateCode' },
  { title: 'City', name: FiltersOfAllJobs.City, key: 'city' },
  { title: 'Customer name', name: FiltersOfAllJobs.Customer, key: 'customerName' },
  { title: 'Schedule name', name: FiltersOfAllJobs.ScheduleName, key: 'scheduleName' },
  { title: 'Vendor Admin name', name: FiltersOfAllJobs.VendorAdminName, key: 'vendorAdminName' },
  { title: 'Schedule Start Date', name: FiltersOfAllJobs.ScheduleStartDate, key: 'scheduleStartDate', format: 'date' },
  { title: 'Schedule End Date', name: FiltersOfAllJobs.ScheduleEndDate, key: 'scheduleEndDate', format: 'date' },
  { title: 'Job Start Date', name: FiltersOfAllJobs.JobStartDate, key: 'jobStartDate', format: 'date' },
  { title: 'Job End Date', name: FiltersOfAllJobs.JobEndDate, key: 'jobEndDate', format: 'date' },
  { title: 'Schedule start time', name: FiltersOfAllJobs.ScheduleStartTime, key: 'scheduleStartTime', format: 'time' },
  { title: 'Schedule end time', name: FiltersOfAllJobs.ScheduleEndTime, key: 'scheduleEndTime', format: 'time' },
  { title: 'Vendor Admin Email', name: FiltersOfAllJobs.VendorAdminEmail, key: 'vendorAdminEmail' },
  { title: 'Work Order Status', name: FiltersOfAllJobs.WorkOrderStatus, key: 'workOrderStatus' },
  { title: 'Created On', name: FiltersOfAllJobs.CreatedOn, key: 'createdOn', format: 'date' },
  { title: 'Work ticket number', name: FiltersOfAllJobs.WorkTicketNumber, key: 'workTicketNumber' },
  { title: 'Schedule type', name: FiltersOfAllJobs.MdScheduleType, key: 'mdScheduleType' },
];

export const UserTypes = {
  Associates: 'associates',
  Supervisors: 'supervisors',
};

export const enum PopupSource {
  Icon = 'icon',
  Action = 'action',
  Delete = 'delete',
  VendorValidation = 'vendorValidation',
}

export enum FilterDropdown {
  Associate = 'Associates',
  Associates = 'Asociados',
}

export enum ActionType {
  Update = 'Update',
  Remove = 'Remove',
}

export enum KeyBoardEvent {
  Enter = 'Enter',
}

export const JobManagementPermissions = {
  ViewSchedule: 'view:schedule',
  ViewVendor: 'view:vendor',
};

export enum CreateUserFields {
  Password = 'Vp@2024',
  Email = 'velociti.com',
}

export const Paths = {
  ManageUser: '/all-jobs/manage-associates-and-supervisors',
  ManageAssociatesAndSupervisors: '/all-jobs/manage-associates-and-supervisors',
  ManageAllJobs: '/all-jobs',
  ModifyGeoFence: '/all-jobs/modify-geofence',
  CreateJobSchedule: '/all-jobs/create-job-schedule',
  AllJobs: 'all-jobs',
  AllSchedules: 'all-schedules',
  ModifyServices: '/all-schedules/modify-services',
  Facilities: '/company/facilities',
  AddFacility: '/add-facility',
  PendingCustomerApprovalOneTimeJobs: '/notifications/pendingCustomerApprovalJobs',
  NavigateToBid: '/create-bid',
  JobDetails: '/job-detail',
  OneTimeJobDetails: '/onetimejobdetails',
  BidApprovalEmail: '/bidApprovalEmail',
  Home: '/home',
  ModifyJobSchedule: '/all-schedules/modify-job-schedule',
  ListView: '/list-view',
};

export const Version = {
  CurrentVersion: '2.10.1',
};

export const ProofSelectionTypes = {
  All: 'all',
  Certain: 'certain',
};

export const ProofActionTypes = {
  Required: 'required',
  Optional: 'optional',
};

export const DebounceTime = 500;

export enum SnackbarSeverity {
  success = 'success',
  error = 'error',
}

export const timeoutValue = 2000;

export const geoFenceRadiusInMiles = [
  {
    value: 0,
    label: '0',
  },
  {
    value: 10,
    label: '10',
  },
  {
    value: 20,
    label: '20',
  },
  {
    value: 30,
    label: '30',
  },
  {
    value: 40,
    label: '40',
  },
  {
    value: 50,
    label: '50',
  },
  {
    value: 60,
    label: '60',
  },
  {
    value: 70,
    label: '70',
  },
  {
    value: 80,
    label: '80',
  },
  {
    value: 90,
    label: '90',
  },
  {
    value: 100,
    label: '100 (miles)',
  },
];

export const mileInMeters = 1609.34;
export const defaultValueInMiles = 0.49;

export const SelectedDuration = {
  FirstQuarter: 'Q1',
  FirstMonth: 'M1',
  FirstWeek: 'W1',
  FirstDay: '1',
};

export const InitialQuarters = {
  Q1: { month: 'M1', week: 'W1', day: '1' },
  Q2: { month: 'M1', week: 'W1', day: '1' },
  Q3: { month: 'M1', week: 'W1', day: '1' },
  Q4: { month: 'M1', week: 'W1', day: '1' },
};

export const DayOptions = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

export const WeekOptions = [
  { value: 'week1', label: 'Week 1' },
  { value: 'week2', label: 'Week 2' },
  { value: 'week3', label: 'Week 3' },
  { value: 'week4', label: 'Week 4' },
  { value: 'week5', label: 'Week 5' },
];

export const refreshDelay = 1000;

export const ReduxStore = {
  appState: 'appState',
  filterState: 'filterState',
  jobSelection: 'jobSelection',
};
export enum WorkDayStatus {
  InProgress = 'P',
  Completed = 'S',
  InComplete = 'I',
  Scheduled = 'A',
  NoShow = 'N',
  Skipped = 'B',
  Rescheduled = 'R',
}

export enum ScheduleLabels {
  InProgress = 'In Progress',
  Completed = 'Completed',
  InComplete = 'Incomplete',
  Scheduled = 'Scheduled',
  NoShow = 'No Show',
  Skipped = 'Skipped',
  Rescheduled = 'Rescheduled',
}

export const ZoomLevelClasses = {
  NormalZoomLevel: 'zoomed-out',
  MediumZoomLevel: 'medium-zoom',
};
export const DEFAULT_PAGE_NUMBER = 1;

export const Types = {
  Number: 'number',
  String: 'string',
  Boolean: 'boolean',
  Object: 'object',
  Array: 'array',
};

export const DefaultLength = 1;

export enum DateRangeOption {
  Today = '1',
  Custom = 'custom',
}

export const OrgTypes = {
  Customer: 'C',
  Vendor: 'V',
};

export const DEFAULT_DATE = '2099-12-31' as unknown as Date;

export const FrequencyScheduleTypes = [
  { value: 'Annual', label: ScheduleTypes.Annual },
  { value: 'Bi-Annual', label: ScheduleTypes.BiAnnual },
  { value: 'Quarterly', label: ScheduleTypes.Quarterly },
  { value: 'Monthly', label: ScheduleTypes.Monthly },
  { value: 'Fortnightly', label: ScheduleTypes.Fortnightly },
  { value: 'Weekly', label: ScheduleTypes.Weekly },
  { value: 'Daily', label: ScheduleTypes.Daily },
];

export const FrequencyShiftTypes = [
  { value: 'Day', label: ShiftTypes.Day },
  { value: 'Night', label: ShiftTypes.Night },
  { value: 'Day & Night', label: ShiftTypes.DayAndNight },
];

export const TaskExecutionType = {
  Original: 'Original',
  Rework: 'Rework',
};
export enum AdhocShiftsFormFields {
  JobNumber = 'jobNumber',
  EmployeeNumber = 'employeeNumber',
  ScheduleDate = 'scheduleStartDate',
  StartTime = 'shiftStartTime',
  EndTime = 'shiftEndTime',
  DaysExtendedBy = 'daysExtendedBy',
  IsBillable = 'isBillable',
  WorkTicketNumber = 'workTicketNumber',
}

export enum PeriodicJobFormFields {
  JobNumber = 'jobNumber',
  Vendor = 'vendor',
  FulfillmentType = 'fulfillmentType',
}

export const VELOCITI_VENDOR_NAME = 'Velociti Services';
export const VELOCITI_NUMBER = '1073';

export const DEFAULT_JOB_NUMBER_LENGTH = 3;
export const DEFAULT_JOB_NUMBER_MAX_LENGTH = 5;

export const velocitiOrganizationId = 'a00aa016-2636-4737-b2a4-b9b0b0a62458';

export const JOB_NAME_FILTER_ID = 'jobName';
export const CREATED_BY_NAME_FILTER_ID = 'createdByName';

export const PROCESSES = [
  {
    processId: '1',
    displayName: 'Home',
    path: '/home',
    state: 'home',
    icon: Icons.HomeIcon,
  },
  {
    processId: '6000',
    displayName: 'Company',
    path: '/company',
    state: 'companyUsers',
    icon: Icons.FacilityIcon,
  },
  {
    processId: '4500',
    displayName: 'Reports',
    path: '/reports',
    state: 'reportsView',
    icon: Icons.GraphIcon,
  },
  {
    processId: '6500',
    displayName: 'Marketplace',
    path: '/marketplace/all-jobs',
    state: 'alljobsview',
    icon: Icons.MarketPlaceIcon,
  },
  {
    processId: '500',
    displayName: 'Jobs',
    path: '/vendor-jobs/self-performed-jobs',
    state: 'vendorJobs',
    icon: Icons.JobsIcon,
  },
  {
    processId: '3500',
    displayName: 'Conversations',
    path: '/normal-conversations/direct',
    state: 'dashboardConversations',
    icon: Icons.ConversationIcon,
  },
  {
    processId: '3000',
    displayName: 'Vendor Management',
    path: '/vendor-management-dashboard',
    state: 'vmsLogin',
    icon: Icons.HandShakeIcon,
  },
];

export const USER_PROCESSES = {
  Home: '1',
  MyCompany: '6000',
  Reports: '4500',
  JobMarketplace: '6500',
  JobBidding: '7000',
  Jobs: '500',
  Conversations: '3500',
  VendorManagementDashboard: '3000',
  JobManagementDashboard: '1000',
  MyAccount: '5500',
  OTJ: '1500',
};

export const PROCESS_EVENTS = {
  DashboardMapView: '2',
  DashboardListView: '3',
  UpcomingShifts: '4',
  JobCompletionTrend: '5',
  JobsToBidOn: '6',

  Facilities: '6001',
  Users: '6002',
  AddFacility: '6003',
  AddUser: '6004',

  Inspection: '4501',
  JobFulfillment: '4502',
  CorrectiveActions: '4503',
  DistanceToFacility: '4504',
  ServiceChannel: '4505',
  LocalSales: '4506',
  ServiceRequests: '4507',

  RecommendedJobs: '6501',
  AllJobs: '6502',
  NotInterestedJobs: '6503',
  AwardedJobs: '6504',
  BidsInProgress: '7001',
  SubmittedBids: '7002',

  ViewAllDetails: '6505',
  NotInterested: '6506',
  Interested: '6507',
  LocalSalesExecution: '6510',

  CreateBid: '7004',
  ContinueBid: '7005',
  ReviseBid: '7006',

  SelfPerformedJobs: '504',
  AllVendorJobs: '506',
  SubContractorBids: '7003',
  ViewJobDetails: '509',
  ModifyJob: '510',
  ScheduleJob: '511',
  MarkTheJobAsComplete: '512',
  DeleteJob: '513',
  ContinueEstimation: '514',
  ViewQuoteDetails: '515',

  ManageJobDetails: '1001',
  ChangeSubVendor: '1002',
  RescheduleShift: '1003',
  CancelShift: '1004',
  ModifySchedule: '1005',
  ManageAssociatesAndSupervisors: '1006',
  AdjustProof: '1007',
  CreateAdhocShifts: '1008',
  RemoveVendor: '1009',
  ReassignVendor: '1010',
  ModifyGeoFence: '1011',
  CreateJobSchedule: '1012',
  CreatePeriodicJobs: '1016',
  MarkJobAsComplete: '1013',
  ViewDetails: '1014',
  ModifyServices: '1015',
  CreateRetrospectiveShift: '1017',
  createOTJ: '1501',
};

export const REPORT_TYPE_TO_EVENT_KEY = {
  Inspection: 'Inspection',
  CorrectiveActions: 'CorrectiveActions',
  DistanceToFacility: 'DistanceToFacility',
  'Job-fulfillment': 'JobFulfillment',
  ServiceChannel: 'ServiceChannel',
  LocalSales: 'LocalSales',
  ServiceRequests: 'ServiceRequests',
};

export const allJobsEventIds = [
  PROCESS_EVENTS.RemoveVendor,
  PROCESS_EVENTS.ReassignVendor,
  PROCESS_EVENTS.ModifyGeoFence,
  PROCESS_EVENTS.CreateJobSchedule,
  PROCESS_EVENTS.CreateAdhocShifts,
  PROCESS_EVENTS.CreateRetrospectiveShift,
];

export const allSchedulesEventIds = [
  PROCESS_EVENTS.RescheduleShift,
  PROCESS_EVENTS.CancelShift,
  PROCESS_EVENTS.ModifySchedule,
  PROCESS_EVENTS.ManageAssociatesAndSupervisors,
  PROCESS_EVENTS.AdjustProof,
];

export const SERVICE_TYPE = {
  janitorial: 'Janitorial',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
};

export enum ServiceCreationType {
  SCHEDULE_LEVEL = 'SCHEDULE_LEVEL',
  CUSTOMER_LEVEL = 'CUSTOMER_LEVEL',
  USER_LEVEL = 'USER_LEVEL',
}

export const SUBTASK_TYPE = {
  PERMANENT: 'permanent',
  SCHEDULE: 'schedule',
};

export const SEARCH_DEBOUNCE_DELAY = 1000;

export const DEFAULT_END_DATE = '2099-12-31';

export const FACILITY_SCOPE = {
  customerOnly: 'CUSTOMER_ONLY',
  all: 'ALL',
};

export const tMobileOrganizationId = '2edbfd6f-95a8-11f0-b68e-12b44a1fb97b';
export const tfiId = '0c1d0ce1-0219-11f1-ad07-12963bf2c063';
export enum mdAttachmentType {
  Audio = 'Audio',
  Video = 'Video',
  Document = 'Document',
  Photo = 'Photo',
  Image = 'Image',
  Pdf = 'Pdf',
}

export type CreateTaskAttachmentInput = {
  attachmentStatus: AttachmentStatus;
  mdAttachmentType: mdAttachmentType;
  fileName: string;
  mdFileContext: mdFileContext;
  fileExtension: string;
  url: string;
  fileHash?: string | null;
  comments?: string | null;
  dataStatus?: string | null;
};

export enum mdFileContext {
  TaskAttachment = 'TaskAttachment',
  IssueAttachment = 'IssueAttachment',
  PreShiftChecklistAttachment = 'PreShiftChecklistAttachment',
  InsuranceAttachment = 'InsuranceAttachment',
  VendorAttachment = 'VendorAttachment',
  ProofOfCompletion = 'ProofOfCompletion',
  ProofOfRework = 'ProofOfRework',
  ProofOfReworkRequested = 'ProofOfReworkRequested',
  ServiceChannel = 'ServiceChannel',
}

export const FILE_EXTENSIONS = {
  pdf: 'pdf',
  video: ['mp4', 'mov', 'avi', 'webm'],
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  docx: 'docx',
} as const;

export const DefaultScheduleName = 'Schedule #1';

export const ACTIVE_WORK_ORDER_STATUSES = [WorkOrderStatus.Accepted, WorkOrderStatus.InProgress];

export const EMPTY_VALUE = 'N/A';

export const WORK_TICKET_STATUS = {
  Waive: 2,
  Skip: 3,
  Complete: 1,
  Pending: 5,
};

export const WORK_TICKET_STATUS_LABEL: Record<number, string> = {
  1: 'Completed',
  2: 'Waived',
  3: 'Skipped',
  5: 'Pending',
};
export const ITEMS_PER_PAGE = 10;
export const PO_LABEL = 'PO Number';

export const DEFAULT_FROM_DATE = '2023-01-01';

export enum ServiceType {
  IncidentTypes = 'IncidentTypes',
}

export const SUPPORT_TYPES = {
  dispatch: 'dispatch',
  accountManager: 'account_manager',
  billing: 'billing',
  generalSupport: 'general_support',
};

export const BILLING_USER_ID = 'a386e153-061f-4417-a461-eb1f903625ec';
export const GENERAL_SUPPORT_USER_ID = 'c74fcccd-229d-11f1-a8b8-12d4dcb9084d';
export const filterDaysOptions = ['1', '-7', '-30', '7', '30'];
export const FILTER_ID = 'hasSignedApproval';

export enum RetrospectiveShiftsFormFields {
  JobNumber = 'jobNumber',
  EmployeeNumber = 'employeeNumber',
  ScheduleDate = 'scheduleStartDate',
  StartTime = 'shiftStartTime',
  EndTime = 'shiftEndTime',
  DaysExtendedBy = 'daysExtendedBy',
  IsBillable = 'isBillable',
  WorkTicketNumber = 'workTicketNumber',
  Status = 'status',
}

export const SALE_TYPES = {
  localSales: 'L',
};

export const MD_VENDOR_TYPE = {
  searchVendors: 'SearchVendors',
};

export const FLOW_TYPES = {
  tag: 'TAG',
  serviceRequest: 'ServiceRequest',
  localSales: 'LocalSales',
};

export const BILLING_INSTRUCTION_FIELDS: Array<{ key: keyof BillingInfo; label: string }> = [
  { key: 'companyName', label: 'Company Name' },
  { key: 'billingContactEmail', label: 'Billing Contact Email Address' },
  { key: 'billingContactFirstName', label: 'Billing Contact First Name' },
  { key: 'billingContactLastName', label: 'Billing Contact Last Name' },
  { key: 'addressLine1', label: 'Address Line 1' },
  { key: 'addressLine2', label: 'Address Line 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'Zip' },
];

export const WEEK_DAY_SHORT_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// JWT bookkeeping claims in the ID token; everything else is a user attribute.
export const ID_TOKEN_SYSTEM_CLAIMS = new Set([
  'aud',
  'auth_time',
  'cognito:groups',
  'event_id',
  'exp',
  'iat',
  'identities',
  'iss',
  'jti',
  'origin_jti',
  'token_use',
]);
